import React, { useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaCrown, FaExclamationTriangle } from "react-icons/fa";
import { useSubscription } from "../context/SubscriptionContext";

// Always-visible top banner that warns the user when their monthly message
// quota is at or near exhaustion. Sits underneath ImpersonationBanner and
// above all routed pages so it's persistent across the whole authenticated
// app surface — the customer can't miss it by navigating away from the
// dashboard.
//
// Thresholds:
//   used >= total    → critical red banner, non-dismissable.
//                      Copy + CTA push the user to upgrade now.
//   used >= 90% of total → amber warning banner. Soft heads-up.
//   below 90%        → render nothing.
//
// Hidden on the pages that already exist to solve the problem
// (/pricing, /settings/billing) so we don't nag the user inside the
// upgrade flow itself, and on unauth pages where we have no subscription.

const HIDDEN_PATH_PREFIXES = [
  "/login",
  "/signup",
  "/forgot-password",
  "/verify-otp",
  "/pricing",
  "/settings",
];

export default function QuotaBanner() {
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();

  const { used, total, isExhausted, isWarning } = useMemo(() => {
    const u = subscription?.usageByKey?.message?.used;
    const t = subscription?.usageByKey?.message?.total;
    if (typeof u !== "number" || typeof t !== "number" || t <= 0) {
      // Unlimited (-1) or missing data — never show the banner.
      return { used: 0, total: 0, isExhausted: false, isWarning: false };
    }
    return {
      used: u,
      total: t,
      isExhausted: u >= t,
      isWarning: u / t >= 0.9 && u < t,
    };
  }, [subscription]);

  if (!isExhausted && !isWarning) return null;

  if (HIDDEN_PATH_PREFIXES.some((p) => location.pathname.startsWith(p))) {
    return null;
  }

  // /pricing is the universal upgrade target — always routed, no role gate.
  // /settings#billing is the internal "view current plan" tab and is admin-only.
  const goUpgrade = () => navigate("/pricing");

  if (isExhausted) {
    return (
      <div className="bg-rose-600 text-white px-4 py-2.5 flex items-center justify-center gap-3 text-sm font-medium z-[9998] relative shadow-sm">
        <FaExclamationTriangle size={14} />
        <span>
          You have used all <strong>{total.toLocaleString()}</strong> messages on your current plan. Kindly upgrade to keep using Walytic — your campaigns will resume automatically once additional credits are available.
        </span>
        <button
          onClick={goUpgrade}
          className="ml-2 px-3 py-1 bg-white text-rose-700 rounded-md text-xs font-semibold hover:bg-rose-50 transition-colors flex items-center gap-1.5"
        >
          <FaCrown size={10} />
          Upgrade Plan
        </button>
      </div>
    );
  }

  const remaining = total - used;
  return (
    <div className="bg-amber-500 text-white px-4 py-2 flex items-center justify-center gap-3 text-sm font-medium z-[9998] relative shadow-sm">
      <FaExclamationTriangle size={13} />
      <span>
        You have <strong>{remaining.toLocaleString()}</strong> of <strong>{total.toLocaleString()}</strong> messages left this month. Consider upgrading to avoid interruptions.
      </span>
      <button
        onClick={goUpgrade}
        className="ml-2 px-3 py-1 bg-white text-amber-700 rounded-md text-xs font-semibold hover:bg-amber-50 transition-colors flex items-center gap-1.5"
      >
        <FaCrown size={10} />
        Upgrade
      </button>
    </div>
  );
}
